// <progress>/<meter> pseudo-elements differ per engine and cannot carry the threshold tick; ARIA roles on divs
// give the same semantics with one styling path.
/* oxlint-disable jsx-a11y/prefer-tag-over-role */
import type { ReactNode } from 'react'
import { cx } from './cx'
import { toneFill, type Tone } from './tone'

const clamp = (v: number) => Math.min(1, Math.max(0, v))
const pct = (v: number) => `${Math.round(clamp(v) * 100)} %`

type ProgressBarProps = {
  /** 0..1 */
  value: number
  label: ReactNode
  /** Right side text, default the percentage ("3 von 5 Schritten"). */
  valueText?: string
  tone?: Tone | 'ink' | 'accent'
  hideLabel?: boolean
}

const fill = (tone: ProgressBarProps['tone']) =>
  tone === 'ink' ? 'bg-ink' : tone === 'accent' || !tone ? 'bg-accent' : toneFill[tone]

/** Progress of a task (upload, monthly close). For a quota against a target, use Meter. */
export function ProgressBar({ value, label, valueText, tone = 'accent', hideLabel }: ProgressBarProps) {
  const text = valueText ?? pct(value)
  return (
    <div className="flex flex-col gap-2">
      <div className={cx('flex items-baseline justify-between gap-3 text-small', hideLabel && 'sr-only')}>
        <span className="text-ink-2">{label}</span>
        <span className="font-medium text-ink tabular">{text}</span>
      </div>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(clamp(value) * 100)}
        aria-valuetext={text}
        aria-label={typeof label === 'string' ? label : undefined}
        className="h-1.5 overflow-hidden rounded-full bg-bg-hover"
      >
        <div
          className={cx('h-full rounded-full transition-[width] duration-150', fill(tone))}
          // Width is data, not design: the one legitimate inline style.
          style={{ width: `${clamp(value) * 100}%` }}
        />
      </div>
    </div>
  )
}

type MeterProps = {
  /** 0..1 */
  value: number
  label: ReactNode
  /** Target, 0..1, drawn as a tick (e.g. 0.75 Anwesenheitsquote). */
  threshold?: number
  /** Default: positive at/above threshold, warning below; neutral without threshold. */
  tone?: Tone
  valueText?: string
  hideLabel?: boolean
  size?: 'sm' | 'md'
}

/** A measured quota (role="meter") with an optional target marker. */
export function Meter({ value, label, threshold, tone, valueText, hideLabel, size = 'md' }: MeterProps) {
  const t: Tone = tone ?? (threshold === undefined ? 'neutral' : value >= threshold ? 'positive' : 'warning')
  const text = valueText ?? pct(value)
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div className={cx('flex items-baseline justify-between gap-3 text-small', hideLabel && 'sr-only')}>
        <span className="truncate text-ink-2">{label}</span>
        <span className="font-medium text-ink tabular">{text}</span>
      </div>
      <div
        role="meter"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(clamp(value) * 100)}
        aria-valuetext={threshold === undefined ? text : `${text}, Ziel ${pct(threshold)}`}
        aria-label={typeof label === 'string' ? label : undefined}
        className={cx('relative rounded-full bg-bg-hover', size === 'sm' ? 'h-1' : 'h-1.5')}
      >
        <div className={cx('h-full rounded-full', toneFill[t])} style={{ width: `${clamp(value) * 100}%` }} />
        {threshold !== undefined && (
          <span
            aria-hidden="true"
            className="absolute -top-[3px] -bottom-[3px] w-[1.5px] -translate-x-1/2 rounded-full bg-ink/45"
            style={{ left: `${clamp(threshold) * 100}%` }}
          />
        )}
      </div>
    </div>
  )
}
